export interface GenreItem {
  name: string;
  value: string;
  detail: any;
  sequence: number;
}

export interface GenreGroup {
  label: string;
  items: GenreItem[];
}

/**
 * Groups a flat list of game genres by their label property.
 * @param list Flat array of genre items from the API
 * @returns Array of grouped genre objects
 */
export function processGenreGroups(list: any[]): GenreGroup[] {
  const groupMap: { [key: string]: GenreItem[] } = {};

  list.forEach((item: any) => {
    if (!groupMap[item.label]) {
      groupMap[item.label] = [];
    }
    groupMap[item.label].push({
      name: item.value,
      value: item.value,
      detail: item.detail,
      sequence: item.sequence,
    });
  });

  return Object.keys(groupMap).map((label) => ({
    label: label,
    items: groupMap[label].sort((a, b) => a.sequence - b.sequence),
  }));
}

/**
 * Handles the logic for filtering items based on a selected category label.
 * @param categoryLabel The selected category label
 * @param gameGenereList The list of grouped genres
 * @returns An object containing the filtered items and the selected label
 */
export function getGenreItemsByCategory(
  categoryLabel: string | null,
  gameGenereList: GenreGroup[]
): { items: GenreItem[]; label: string | null } {
  if (categoryLabel) {
    const category = gameGenereList.find((g) => g.label === categoryLabel);
    if (category) {
      return {
        items: category.items,
        label: category.label,
      };
    }
  }
  return {
    items: [],
    label: null,
  };
}

/**
 * Finds the category that contains a specific genre value.
 * Used during form patching/initialization.
 * @param genreValue The specific genre value to look for
 * @param gameGenereList Total list of grouped genres
 */
export function findCategoryByGenre(
  genreValue: string,
  gameGenereList: GenreGroup[]
): GenreGroup | undefined {
  return gameGenereList.find((group) =>
    group.items.some((item) => item.value === genreValue)
  );
}
